import { useTheme } from '@approvalmax/theme';
import { hooks } from '@approvalmax/utils';
import { ui } from 'modules/common';
import { EyeShowOffIcon, EyeShowOnIcon } from 'modules/sprites';
import React, { FC, memo, useCallback, useRef, useState } from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useMedia } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { notificationService } from 'services/notification';

import PhoneField from '../PhoneField/PhoneField';
import QualificationQuestions from '../QualificationQuestions/QualificationQuestions';
import { answersDataStorageKey, formDataStorageKey } from './ProfileSetupForm.constants';
import { passwordIsValid, qualificationQuestionsAnswersIsValid } from './ProfileSetupForm.helpers';
import { useAnswersSyncState, useFormSyncState } from './ProfileSetupForm.hooks';
import { messages } from './ProfileSetupForm.messages';
import {
    AnimatedBox,
    EmailSection,
    MarketingCheckboxEditor,
    NameSection,
    PasswordEditorWrp,
    PasswordTextEditor,
    PasswordToggleButton,
    PasswordValidation,
    PhoneSection,
    ProgressContainer,
    QuestionsSection,
    Root,
    SignUpButton,
    SsoDelimiter,
    StyledSignupConsent,
    StyledSsoPanel,
    StyledTextEditor,
    Title,
} from './ProfileSetupForm.styles';
import { ProfileSetupFormProps } from './ProfileSetupForm.types';

const ProfileSetupForm: FC<ProfileSetupFormProps> = memo((props) => {
    const {
        email,
        isLoading,
        profileData,
        showPhoneField,
        showConsent,
        showQuestions,
        showPasswordField,
        showSsoPanel,
        showProgress,
        onDataChange,
        onSubmit,
        saveStateInLocalStorage,
        ...otherProps
    } = props;

    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [showPasswordValidationSection, setShowPasswordValidationSection] = useState(false);

    const { animatedStyles, animatedRef } = hooks.useAnimatedHeight(showPasswordValidationSection);

    const [showPassword, setShowPassword] = useState(false);

    const passwordEditor = useRef<HTMLInputElement>(null);

    const toggleShowPasswordClick = useCallback(() => {
        setShowPassword(!showPassword);
        passwordEditor.current?.focus();
    }, [showPassword]);
    const toggleShowPasswordMouse = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault(); // for save cursor position
            setShowPassword(!showPassword);
        },
        [showPassword]
    );

    const { changeFirstName, changeLastName, changePhone, changePassword, isPhoneTouched, setIsPhoneTouched } =
        useFormSyncState(profileData, onDataChange, showPhoneField, saveStateInLocalStorage);

    const { qualificationAnswersInitial, qualificationQuestionsAnswers, handleChangeQualificationQuestionsAnswers } =
        useAnswersSyncState(showQuestions);

    const [consentMarketingAccepted, setConsentMarketingAccepted] = useState(false);

    const prevInvalidPhone = useRef('');

    const handleSignUp = useCallback(() => {
        if (isLoading) {
            return;
        }

        const isPhoneValid = !showPhoneField || isPossiblePhoneNumber(profileData.phone || '');
        const isPasswordValid = !showPasswordField || passwordIsValid(profileData.password);

        const isValid = Boolean(
            isPasswordValid && profileData.firstName.trim() && profileData.lastName.trim() && isPhoneValid
        );

        const answersIsValid =
            !showQuestions ||
            (qualificationQuestionsAnswers && qualificationQuestionsAnswersIsValid(qualificationQuestionsAnswers));

        if (!isValid || !answersIsValid) {
            if (!isPhoneValid) {
                if (profileData.phone && profileData.phone !== prevInvalidPhone.current) {
                    prevInvalidPhone.current = profileData.phone;
                    amplitudeService.sendData('signup: error in phone validation occurred', {
                        value: profileData.phone,
                    });
                }

                notificationService.showErrorToast(messages.phoneNumberIsInvalid);
            }

            if (!answersIsValid) {
                notificationService.showErrorToast(messages.qualificationQuestionsAnswersIsInvalid);
            }

            if (!isPasswordValid) {
                setShowPasswordValidationSection(true);
            }

            setShowValidationErrors(true);
        } else {
            onSubmit({ qualificationQuestionsAnswers, consentMarketingAccepted });
            localStorage.removeItem(formDataStorageKey);
            localStorage.removeItem(answersDataStorageKey);
        }
    }, [
        consentMarketingAccepted,
        isLoading,
        onSubmit,
        profileData.firstName,
        profileData.lastName,
        profileData.password,
        profileData.phone,
        qualificationQuestionsAnswers,
        showPasswordField,
        showPhoneField,
        showQuestions,
    ]);

    const { theme } = useTheme();
    const isMobileScreen = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);

    return (
        <Root {...otherProps}>
            {showProgress && <ProgressContainer currentStep={2} />}

            <Title>{messages.profileSetupTitle}</Title>

            {email && <EmailSection className='fs-mask'>{email}</EmailSection>}

            <NameSection>
                <StyledTextEditor
                    theme='auth'
                    placeholder={messages.placeholderFirstName}
                    focusOnMount
                    spellCheck={false}
                    invalid={showValidationErrors && !profileData.firstName.trim()}
                    value={profileData.firstName}
                    onChange={changeFirstName}
                />

                <StyledTextEditor
                    theme='auth'
                    placeholder={messages.placeholderLastName}
                    spellCheck={false}
                    value={profileData.lastName}
                    invalid={showValidationErrors && !profileData.lastName.trim()}
                    onChange={changeLastName}
                />
            </NameSection>

            {showPhoneField && (
                <PhoneSection>
                    <PhoneField
                        value={profileData.phone}
                        onChange={changePhone}
                        isPhoneTouched={isPhoneTouched}
                        setIsPhoneTouched={setIsPhoneTouched}
                        invalid={showValidationErrors && !isPossiblePhoneNumber(profileData.phone || '')}
                    />
                </PhoneSection>
            )}

            {showPasswordField && (
                <>
                    <PasswordEditorWrp>
                        <PasswordTextEditor
                            ref={passwordEditor}
                            placeholder={messages.placeholderPassword}
                            invalid={showValidationErrors && !passwordIsValid(profileData.password)}
                            spellCheck={false}
                            maxLength={180}
                            value={profileData.password ?? ''}
                            onChange={changePassword}
                            type={showPassword ? 'text' : 'password'}
                            onFocus={() => setShowPasswordValidationSection(true)}
                            autoComplete='new-password'
                        />

                        <PasswordToggleButton onMouseDown={toggleShowPasswordMouse} onClick={toggleShowPasswordClick}>
                            {showPassword ? (
                                <EyeShowOffIcon width={20} height={14} />
                            ) : (
                                <EyeShowOnIcon width={22} height={12} />
                            )}
                        </PasswordToggleButton>
                    </PasswordEditorWrp>

                    <AnimatedBox style={animatedStyles}>
                        <div ref={animatedRef}>
                            {!passwordIsValid(profileData.password) && (
                                <PasswordValidation>
                                    <ui.PasswordValidationSection
                                        layout='vertical'
                                        password={profileData.password ?? ''}
                                        highlightInvalidCriteria={showValidationErrors}
                                        hidePassed
                                    />
                                </PasswordValidation>
                            )}
                        </div>
                    </AnimatedBox>
                </>
            )}

            {showQuestions && (
                <QuestionsSection>
                    <QualificationQuestions
                        initialData={qualificationAnswersInitial}
                        onChange={handleChangeQualificationQuestionsAnswers}
                    />
                </QuestionsSection>
            )}

            {showConsent && (
                <MarketingCheckboxEditor
                    checkboxAlignment='top'
                    onChange={setConsentMarketingAccepted}
                    value={consentMarketingAccepted}
                >
                    {messages.consentMarketingText}
                </MarketingCheckboxEditor>
            )}

            <SignUpButton execute={handleSignUp} disabled={isLoading}>
                {messages.signupButtonText}
            </SignUpButton>

            {showConsent && <StyledSignupConsent buttonText={messages.signupButtonText} />}

            {showSsoPanel && (
                <>
                    <SsoDelimiter>{messages.ssoPanelCaption}</SsoDelimiter>

                    <StyledSsoPanel
                        variant='signup'
                        disabled={isLoading}
                        allowedEmail={email}
                        width={isMobileScreen ? 'auto' : undefined}
                        afterLoginRedirectUrl={window.location.href}
                        afterSignUpRedirectUrl={window.location.href}
                    />
                </>
            )}
        </Root>
    );
});

export default ProfileSetupForm;
